/**
 * Appoint Director — 선택한 컨택트(officer-list) → e-Filing contactData.
 *   AppointDirectorForm.vue(구 폼) 의 같은 이름 함수를 스튜디오 패널에서도 쓰도록 옮긴 것(로직 동일).
 */
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';

function pickFirstText(...values: unknown[]): string {
  for (const value of values) {
    const text = value === null || value === undefined ? '' : String(value).trim();
    if (text) return text;
  }
  return '';
}

function hasAnyText(values: unknown[]): boolean {
  return values.some((value) => pickFirstText(value).length > 0);
}

function normalizeSelectedAddress(
  raw: Record<string, any>,
  kind: 'primary' | 'alternate',
): {
  typeCd: string;
  postalCode: string;
  blockNo: string;
  streetName: string;
  buildingName: string;
  addrLevel: string;
  addrUnit: string;
  foreignAddressLine1: string;
  foreignAddressLine2: string;
} {
  const isPrimary = kind === 'primary';
  const parts = (isPrimary ? raw.primaryAddressParts : raw.alternateAddressParts) || {};

  const postalCode = pickFirstText(
    isPrimary ? raw.primaryPostalCode : raw.alternatePostalCode,
    parts.postalCode,
    isPrimary ? raw.POSTAL_CODE : raw.ALT_POSTAL_CODE,
    isPrimary ? raw.postalCode : raw.altPostalCode,
  );
  const blockNo = pickFirstText(
    isPrimary ? raw.primaryBlockNo : raw.alternateBlockNo,
    parts.blockNo,
    isPrimary ? raw.BLOCK_NO : raw.ALT_BLOCK_NO,
    isPrimary ? raw.blockNo : raw.altBlockNo,
  );
  const streetName = pickFirstText(
    isPrimary ? raw.primaryStreetName : raw.alternateStreetName,
    parts.streetName,
    isPrimary ? raw.STREET_NAME : raw.ALT_STREET_NAME,
    isPrimary ? raw.streetName : raw.altStreetName,
  );
  const buildingName = pickFirstText(
    isPrimary ? raw.primaryBuildingName : raw.alternateBuildingName,
    parts.buildingName,
    isPrimary ? raw.BUILDING_NAME : raw.ALT_BUILDING_NAME,
    isPrimary ? raw.buildingName : raw.altBuildingName,
  );
  const addrLevel = pickFirstText(
    isPrimary ? raw.primaryAddrLevel : raw.alternateAddrLevel,
    parts.addrLevel,
    isPrimary ? raw.ADDR_LEVEL : raw.ALT_ADDR_LEVEL,
    isPrimary ? raw.addrLevel : raw.altAddrLevel,
  );
  const addrUnit = pickFirstText(
    isPrimary ? raw.primaryAddrUnit : raw.alternateAddrUnit,
    parts.addrUnit,
    isPrimary ? raw.ADDR_UNIT : raw.ALT_ADDR_UNIT,
    isPrimary ? raw.addrUnit : raw.altAddrUnit,
  );
  const foreignAddressLine1 = pickFirstText(
    isPrimary ? raw.primaryForeignAddressLine1 : raw.alternateForeignAddressLine1,
    parts.foreignAddressLine1,
    isPrimary ? raw.FOREIGN_ADDRESS_LINE_1 : raw.ALT_FOREIGN_ADDRESS_LINE_1,
    isPrimary ? raw.foreignAddressLine1 : raw.altForeignAddressLine1,
  );
  const foreignAddressLine2 = pickFirstText(
    isPrimary ? raw.primaryForeignAddressLine2 : raw.alternateForeignAddressLine2,
    parts.foreignAddressLine2,
    isPrimary ? raw.FOREIGN_ADDRESS_LINE_2 : raw.ALT_FOREIGN_ADDRESS_LINE_2,
    isPrimary ? raw.foreignAddressLine2 : raw.altForeignAddressLine2,
  );

  const hasLocalAddress = hasAnyText([postalCode, blockNo, streetName, buildingName, addrLevel, addrUnit]);
  const hasForeignAddress = hasAnyText([foreignAddressLine1, foreignAddressLine2]);

  let typeCd = pickFirstText(
    isPrimary ? raw.primaryAddressTypeCd : raw.alternateAddressTypeCd,
    parts.addressTypeCd,
    isPrimary ? raw.ADDRESS_TYPE_CD : raw.ALTERNATE_ADDRESS_CD,
    isPrimary ? raw.addressTypeCd : raw.alternateAddressCd,
  );

  // 이미 저장된 B07 코드는 우선 — 아래 휴리스틱이 Service/Foreign(B07002)를 Local(B07001)로 덮어쓰는 것 방지
  if (typeCd !== 'B07001' && typeCd !== 'B07002') {
    if (hasLocalAddress && !hasForeignAddress) typeCd = 'B07001';
    else if (hasForeignAddress && !hasLocalAddress) typeCd = 'B07002';
    else typeCd = hasLocalAddress ? 'B07001' : 'B07002';
  }

  const combinedAddress = pickFirstText(
    isPrimary ? raw.primaryAddress : raw.alternateAddress,
    isPrimary ? raw.address : raw.serviceAddress,
  );

  /** 이미 FOREIGN 줄이 있으면 한 줄 텍스트(combined)를 1줄에 넣지 않음 — 중복·잘못된 분할 방지 */
  const useCombinedForForeignOnly =
    typeCd === 'B07002' &&
    !hasForeignAddress &&
    !hasLocalAddress &&
    !!String(combinedAddress || '').trim();

  return {
    typeCd: typeCd || 'B07001',
    postalCode,
    blockNo,
    streetName,
    buildingName,
    addrLevel,
    addrUnit,
    foreignAddressLine1:
      typeCd === 'B07002'
        ? foreignAddressLine1 || (useCombinedForForeignOnly ? combinedAddress : '')
        : '',
    foreignAddressLine2: typeCd === 'B07002' ? foreignAddressLine2 : '',
  };
}

export function buildContactDataFromSelectedPerson(person: OfficerDetail): Record<string, any> {
  const raw = person as any;
  const primaryAddress = normalizeSelectedAddress(raw, 'primary');
  const alternateAddress = normalizeSelectedAddress(raw, 'alternate');

  return {
    contactCd: raw.contactCd || '',
    CONTACT_NAME: raw.CONTACT_NAME || raw.contactName || raw.name || '',
    FIRST_NAME: raw.FIRST_NAME || raw.firstName || '',
    MIDDLE_NAME: raw.MIDDLE_NAME || raw.middleName || '',
    SURNAME: raw.SURNAME || raw.surname || raw.lastName || '',
    ID_TYPE_CD: raw.ID_TYPE_CD || raw.idTypeCd || '',
    CONTACT_REG_NO: raw.CONTACT_REG_NO || raw.contactRegNo || raw.nricNo || raw.passportNo || '',
    NATIONALITY_CD: raw.NATIONALITY_CD || raw.nationalityCd || '',
    DATE_OF_BIRTH: (raw.DATE_OF_BIRTH || raw.dateOfBirth || raw.dob || '').substring(0, 10),
    CATEGORY_CD: raw.CATEGORY_CD || raw.categoryCd || 'B09002',
    ADDRESS_TYPE_CD: primaryAddress.typeCd,
    POSTAL_CODE: primaryAddress.postalCode,
    BLOCK_NO: primaryAddress.blockNo,
    STREET_NAME: primaryAddress.streetName,
    BUILDING_NAME: primaryAddress.buildingName,
    ADDR_LEVEL: primaryAddress.addrLevel,
    ADDR_UNIT: primaryAddress.addrUnit,
    FOREIGN_ADDRESS_LINE_1: primaryAddress.foreignAddressLine1,
    FOREIGN_ADDRESS_LINE_2: primaryAddress.foreignAddressLine2,
    ALTERNATE_ADDRESS_CD: alternateAddress.typeCd,
    ALT_POSTAL_CODE: alternateAddress.postalCode,
    ALT_BLOCK_NO: alternateAddress.blockNo,
    ALT_STREET_NAME: alternateAddress.streetName,
    ALT_BUILDING_NAME: alternateAddress.buildingName,
    ALT_ADDR_LEVEL: alternateAddress.addrLevel,
    ALT_ADDR_UNIT: alternateAddress.addrUnit,
    ALT_FOREIGN_ADDRESS_LINE_1: alternateAddress.foreignAddressLine1,
    ALT_FOREIGN_ADDRESS_LINE_2: alternateAddress.foreignAddressLine2,
    COUNTRY_CD: raw.COUNTRY_CD || raw.countryCd || '',
    AREA_CODE: raw.AREA_CODE || raw.areaCode || '',
    MOBILE_NO: raw.MOBILE_NO || raw.mobileNo || raw.phone || '',
    CONTACT_EMAIL: raw.CONTACT_EMAIL || raw.contactEmail || raw.email || '',
  };
}
